import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

/**
 * The platform's own figures, and the three windows they are not.
 *
 * `GET /super-admin/stats/platform` answers `revenueLast30Days` computed as **`current_date - 30`
 * excluding cancelled orders**. Measured to the cent against the databases: 44 807,65 €. The two windows
 * a rewrite would reach for first give **44 761,45 €** (`now() - interval '30 days'`) and **45 161,90 €**
 * (the same 30 days *including* cancellations) — 400 € apart. So the figure is read, never recomputed,
 * and the screen says which window it is.
 *
 * `mrr` is exactly 3 × 499,00 €, the `pizzamajstro` package's monthly price, and the other two packages
 * have no franchise on them. That is worth stating on screen too: an MRR that looks like a single
 * package's price times a headcount is easy to mistake for a placeholder.
 */

export interface PlatformStats {
  totalFranchises: number
  activeFranchises: number
  mrrCents: number
  tierMix: { slug: string; count: number }[]
  ordersToday: number
  revenueLast30DaysCents: number
  /**
   * How many active franchises did not answer - §3.164, FB-874.
   *
   * The two figures above are summed one tenant database at a time and the server survives one being
   * down on purpose, so they come back **smaller** rather than absent. Nought is the ordinary state.
   */
  unreachableFranchises: number
}

function count(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function platformStatsOf(payload: unknown): PlatformStats {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const mix = typeof row.tierMix === 'object' && row.tierMix !== null ? (row.tierMix as Record<string, unknown>) : {}
  return {
    totalFranchises: count(row.totalFranchises),
    activeFranchises: count(row.activeFranchises),
    mrrCents: parseMoneyToCents(row.mrr as string | number | null),
    // Biggest first: a tier mix read top-down should start with where the platform actually is.
    tierMix: Object.entries(mix)
      .map(([slug, value]) => ({ slug, count: count(value) }))
      .sort((left, right) => right.count - left.count),
    ordersToday: count(row.ordersToday),
    unreachableFranchises: count(row.unreachableFranchises),
    revenueLast30DaysCents: parseMoneyToCents(row.revenueLast30Days as string | number | null),
  }
}

/** Said under the revenue figure, because three plausible windows differ by 400 € on this data. */
export function revenueWindowNote(): string {
  return tt('Za posledných 30 dní, bez zrušených objednávok.')
}

/**
 * Franchises that are counted in the total but are not live.
 *
 * `totalFranchises` and `activeFranchises` are both 3 here, so the difference is zero — which is exactly
 * when a screen stops showing it and nobody notices the day it stops being zero.
 */
export function inactiveCount(stats: PlatformStats): number {
  return Math.max(0, stats.totalFranchises - stats.activeFranchises)
}

export function inactiveNote(stats: PlatformStats): string {
  const inactive = inactiveCount(stats)
  if (inactive === 0) return ''
  return `${formatCount(inactive)} ${tt('franšíz je neaktívnych a do MRR sa nepočítajú.')}`
}

/**
 * Said under the figures when a tenant database did not answer - §3.164, FB-874.
 *
 * **Not an `isError` state, which is why nothing had caught it.** `platformStats` sums the orders and
 * the 30-day revenue one tenant at a time and survives one being down deliberately - a dashboard that
 * goes blank during an incident is one nobody can use - so the request succeeds and the two figures
 * simply come back **short**. There is no failed read for a screen to consult.
 *
 * **It names the two figures rather than saying "some data is missing"**, because those are the only
 * two summed per tenant: `Franšízy`, MRR and the tier mix all come from the central registry and are
 * complete either way, so a blanket warning would put doubt on figures that are fine.
 *
 * **And it points at Štatistiky, which really does name them** - that screen reads each row's own
 * `error` through `unreachable`. §9.556 is the entry about an instruction sending somebody to a screen
 * that cannot help, so the destination was checked rather than assumed.
 */
export function unreachableNote(stats: PlatformStats): string {
  if (stats.unreachableFranchises === 0) return ''
  return `${formatCount(stats.unreachableFranchises)} ${tt('franšíz neodpovedalo, takže Objednávky dnes a Tržba za 30 dní sú nižšie, než v skutočnosti sú. Ktoré to sú, je vidieť v Štatistikách.')}`
}

/** `1 497,00 € / mesiac` — the figure with its period, since MRR without one is just a number. */
export function mrrNote(stats: PlatformStats): string {
  return `${formatCents(stats.mrrCents)} / ${tt('mesiac')}`
}
